/**
 * Which inputs of a Deal Model source a property may override.
 *
 * An input whose `resolution.propertyOverrideKey` is set accepts a
 * per-property value. The stock Financial models leave works budgets and GDV
 * to the common personal controls (refurbishment and value), so those keys are
 * not offered here for them. Ported unchanged from the workbench's Deals v2
 * source helpers; the source document is the only input.
 */
import { HOME_BUYER_MODEL_ID } from '../../experiences/registry'

interface JsonObject { [key: string]: unknown }

const STOCK_FINANCIAL_MODEL_IDS = new Set([
  HOME_BUYER_MODEL_ID,
  '370f437f-96fa-4754-86bd-f59ee80bf60f',
  'b44479f1-e327-4821-8492-6054d1e3c93c',
  'b5d738a9-ebe1-4609-bd49-5fd932f25e0f',
])
const PERSONAL_WORKS_BUDGET_KEYS = new Set(['refurbCost', 'lightRefurbCost', 'fullRefurbCost', 'gdv', 'postWorksValue'])

function object(value: unknown): JsonObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : null
}

function text(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

export interface PropertyOverrideDescriptor {
  key: string
  inputId: string
  label: string
  kind: string
  nullable: boolean
  enumValues: string[]
  defaultValue: unknown
}

export function recipePropertyOverrides(source: unknown): PropertyOverrideDescriptor[] {
  const root = object(source)
  const inputs = root?.inputs
  if (!Array.isArray(inputs)) return []
  const personalWorksBudget = STOCK_FINANCIAL_MODEL_IDS.has(text(root?.logicalModelId) ?? '')
  const descriptors = new Map<string, PropertyOverrideDescriptor>()
  for (const raw of inputs) {
    const input = object(raw)
    const key = text(object(input?.resolution)?.propertyOverrideKey)
    const inputId = text(input?.id)
    if (!key || !inputId || !input) continue
    // Common personal controls own stock works budgets and GDV.
    if (personalWorksBudget && PERSONAL_WORKS_BUDGET_KEYS.has(key)) continue
    const semantic = object(input.semanticType)
    descriptors.set(key, {
      key,
      inputId,
      label: text(input.label) ?? key,
      kind: text(semantic?.kind) ?? 'unknown',
      nullable: semantic?.nullable === true,
      enumValues: Array.isArray(semantic?.enumValues) ? semantic.enumValues.filter((value): value is string => typeof value === 'string') : [],
      defaultValue: input.defaultValue,
    })
  }
  return [...descriptors.values()]
}

/**
 * Whether a stock Financial model leaves this input to a common personal
 * control (the refurbishment selector or the personal GDV editor) instead of
 * a property override. Mirrors the server: the works-budget keys, and every
 * key whose fact is the card GDV, are refused for the stock models.
 */
export function isPersonalControlOverride(logicalModelId: string, input: { propertyOverrideKey: string | null; factPath: string | null }): boolean {
  if (!STOCK_FINANCIAL_MODEL_IDS.has(logicalModelId) || !input.propertyOverrideKey) return false
  return PERSONAL_WORKS_BUDGET_KEYS.has(input.propertyOverrideKey) || input.factPath === 'card.valuation.gdvPence'
}

export function isStockFinancialModel(logicalModelId: string): boolean { return STOCK_FINANCIAL_MODEL_IDS.has(logicalModelId) }
